// The header and truncated-cell popovers wear the shared info-tooltip surface (shadow, size) plus
// the dashboard chart-tooltip tweaks, so load both here rather than relying on some other component
// on the page having loaded them.
import '@Components/InfoTooltip/InfoTooltip.scss';
import './ChartRenderer.scss';
import './chart-tooltip.scss';
import cn from 'classnames';
import { useState, useRef, useEffect, useMemo, PropsWithChildren } from 'react';
import { Overlay, OverlayTrigger, Popover, Table, Form } from 'react-bootstrap';
import { MdArrowUpward, MdArrowDownward } from 'react-icons/md';
import {
  ChartConfig,
  QueryResponse,
  FieldMetaCtx,
  CellValue
} from '@blisspointmedia/bpm-types/dist/dashboarding';
import { QueryConfig } from '@blisspointmedia/bpm-types/dist/querying/queryConfig';
import { useQuery } from '@tanstack/react-query';
import { Skeleton } from '@Components/Skeleton';
import { Placement } from '@Components/designConstants';
import { awaitJSON, DashboardingLambdaFetch } from '../../utils/fetch-utils';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import {
  prepareTable,
  filterRowsBySearch,
  buildSearchTotalsConfig,
  getTableSearchScope,
  normalizeSearchTerm,
  PreparedColumnGroup,
  PreparedTableColumn,
  PreparedTableFooter,
  ColumnSort,
  searchTotalsUnavailable,
  withAppliedDashboardFilters,
  tableSpansBlocks,
  UNMERGED_TABLE_MESSAGE
} from './chart-renderer-utils';
import { ChartPlaceholder } from './ChartPlaceholder';

interface TableRendererProps {
  config: ChartConfig;
  data: QueryResponse;
  className?: string;
  showTitle?: boolean;
  ctx?: FieldMetaCtx;
}

// The four things a footer cell can be showing. The three non-values are NOT interchangeable and must
// not be collapsed: an em-dash says there is nothing to total, N/A says the total could not be
// computed, and a skeleton says it is on its way. Each answers a different question the reader has.
const FooterState = {
  VALUES: 'values',
  LOADING: 'loading',
  EMPTY: 'empty',
  UNAVAILABLE: 'unavailable'
} as const;
type FooterStateType = (typeof FooterState)[keyof typeof FooterState];

// Not `0`: zero asserts a fact about data that does not exist, and would render as "$0.00" / "0.00x".
const NO_MATCHES_LABEL = '—';
const UNAVAILABLE_LABEL = 'N/A';

// One footer cell's text: the SQL grand total for a metric column, the "Total" label on the first
// dimension column, blank otherwise. The VALUE always comes from the sibling 0-dimension totals
// query — the client never aggregates a footer.
const footerCellContent = (col: PreparedTableColumn, footer: PreparedTableFooter): string => {
  if (col.key in footer.cells) return col.formatValue(footer.cells[col.key]);
  return col.key === footer.labelKey ? footer.label : '';
};

/**
 * The searched grand total: a second, FOOTER-ONLY query that re-runs the chart's 0-dimension totals
 * query scoped by the active search.
 *
 * Deliberately its own `useQuery` rather than part of the chart's data fetch. The body's `q0` cache
 * entry is never invalidated by this, so the table never re-renders or re-fetches its rows while the
 * user types — only the footer row changes. The 300ms search debounce upstream doubles as this
 * query's request throttle, and React Query's keying discards responses for terms the user has already
 * typed past.
 *
 * Keyed on the serialized config rather than (chartId, term): the config already encodes the company,
 * the chart, the term AND any active date/kpi/breakdown override, so two views of the same chart at
 * different date ranges cannot collide on one cache entry.
 */
const useSearchTotals = (company: string, totalsConfig: QueryConfig | null) =>
  useQuery({
    queryKey: ['dashboarding-table-search-totals', company, JSON.stringify(totalsConfig)],
    enabled: Boolean(company) && Boolean(totalsConfig),
    queryFn: async () => {
      const res = await DashboardingLambdaFetch('/getData', {
        method: 'POST',
        body: { company, config: totalsConfig }
      });
      return awaitJSON<QueryResponse>(res);
    }
  });

// Hook that manages the search term + its debounce, and narrows the displayed rows to those matching
// across `rowKeys` — the table's searchable dimension columns, the same set the totals query is
// scoped by. The debounced term is what both halves read, so body and footer always describe one term.
function useFilteredTable(allRows: Record<string, CellValue>[], rowKeys: string[]) {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Debounce effect: update debouncedSearchTerm after user stops typing
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchTerm]);

  // Filter rows when debouncedSearchTerm changes
  const filteredRows = useMemo(
    () => filterRowsBySearch(allRows, rowKeys, debouncedSearchTerm),
    [allRows, rowKeys, debouncedSearchTerm]
  );

  return {
    searchTerm,
    debouncedSearchTerm,
    filteredRows,
    handleSearch: setSearchTerm
  };
}

interface FooterCellProps {
  col: PreparedTableColumn;
  // The footer to read values AND shape from: the searched totals when they have resolved, otherwise
  // the idle one, whose `cells` still say WHICH metric columns carry a total at all.
  footer: PreparedTableFooter;
  state: FooterStateType;
}

const FooterCell = ({ col, footer, state }: FooterCellProps) => {
  // The "Total" label, blank dimension cells, and metric columns that opted out via `aggregate:
  // 'none'` read the same in every state — no search changes them, so they must not flicker.
  if (!col.isMetric || !(col.key in footer.cells)) {
    return footerCellContent(col, footer);
  }
  if (state === FooterState.LOADING) {
    return (
      <div className='table-renderer-total-skeleton'>
        <Skeleton>
          <rect x='0' y='0' width='100%' height='100%' />
        </Skeleton>
      </div>
    );
  }
  if (state === FooterState.EMPTY) return NO_MATCHES_LABEL;
  if (state === FooterState.UNAVAILABLE) return UNAVAILABLE_LABEL;
  return footerCellContent(col, footer);
};

interface TableFooterRowProps {
  columns: PreparedTableColumn[];
  footer: PreparedTableFooter;
  state: FooterStateType;
}

const TableFooterRow = ({ columns, footer, state }: TableFooterRowProps) => (
  <tr className='table-renderer-total'>
    {columns.map(col => (
      <td key={col.key} className={cn({ 'is-numeric': col.isMetric })}>
        <FooterCell col={col} footer={footer} state={state} />
      </td>
    ))}
  </tr>
);

interface TableSuperHeaderRowProps {
  groups: PreparedColumnGroup[];
}

// The superheader row above the column headers: one cell per run of adjacent same-`group` columns,
// blank over ungrouped ones.
const TableSuperHeaderRow = ({ groups }: TableSuperHeaderRowProps) => (
  <tr className='table-renderer-super-header-row'>
    {groups.map(group => (
      <th
        key={group.key}
        colSpan={group.span}
        scope='colgroup'
        className='table-renderer-super-header'
      >
        {group.label && <div className='table-renderer-super-header-label'>{group.label}</div>}
      </th>
    ))}
  </tr>
);

// The rowLimit note. When a grand total is on screen it has to say what the total COVERS: the total
// is a full-dataset aggregate, so a table showing 10 of 500 rows above it otherwise reads as broken
// arithmetic rather than as a deliberately wider scope.
const truncationNote = (
  shown: number,
  truncated: number,
  hasFooter: boolean,
  isSearching: boolean
): string => {
  const note = `Showing ${shown.toLocaleString()} rows; ${truncated.toLocaleString()} more hidden.`;
  if (!hasFooter) return note;
  const scope = isSearching ? 'every matching row' : 'every row';
  return `${note} The total covers ${scope}, including those hidden here.`;
};

interface FooterStateInput {
  isSearching: boolean;
  hasMatches: boolean;
  isFetching: boolean;
  isError: boolean;
  searchedFooter?: PreparedTableFooter;
}

// Which state the footer is in. Order matters: "no matches" is answered before anything is fetched
// (the browser already knows there is nothing to total), and a failure never falls back to the
// unfiltered total — that would be the shadowing bug this work removes, wearing a new costume.
const resolveFooterState = ({
  isSearching,
  hasMatches,
  isFetching,
  isError,
  searchedFooter
}: FooterStateInput): FooterStateType => {
  if (!isSearching) return FooterState.VALUES;
  if (!hasMatches) return FooterState.EMPTY;
  if (isFetching) return FooterState.LOADING;
  if (isError || !searchedFooter) return FooterState.UNAVAILABLE;
  return FooterState.VALUES;
};

interface HeatCellProps extends PropsWithChildren {
  col: PreparedTableColumn;
  value: CellValue;
}

// A shaded cell wears its color on an inner chip rather than on the `td`, so the fill gets the
// chip's rounded geometry and the `td` keeps owning the table's cell padding (which becomes the gap
// between chips). Two cases render the value bare, with NO wrapper element: a column the registry
// doesn't shade, and a cell with no number to shade inside one it does.
const HeatCell = ({ col, value, children }: HeatCellProps) => {
  const heat = col.heatStyle?.(value);
  if (!heat) return children;
  return (
    <span
      className='table-renderer-heat'
      // Data-derived per cell, so inline is correct here; the chip's static geometry is in SCSS.
      // An inset box-shadow rather than a border for the ring: it follows the border radius, costs
      // no layout, and stays inside the chip so neighbouring chips can't collide.
      style={{
        backgroundColor: heat.backgroundColor,
        ...(heat.ring ? { boxShadow: `inset 0 0 0 1px ${heat.ring}` } : {})
      }}
    >
      {children}
    </span>
  );
};

interface CellTextProps {
  col: PreparedTableColumn;
  value: CellValue;
}

// A body cell's text. Dates and numbers render bare; a text column (campaign / ad set / ad names)
// is capped in width (see `.table-renderer-cell-text`) so one very long name can't stretch its
// whole column, and ends in an ellipsis once it overflows.
const CellText = ({ col, value }: CellTextProps) => {
  if (col.isDate) return String(value ?? '');
  if (col.isMetric) return col.formatValue(value);
  return <TruncatedCellText text={col.formatValue(value)} />;
};

interface TruncatedCellTextProps {
  text: string;
}

// Shows the full text in a tooltip on hover, only when it's actually cut off. Measured at hover time
// rather than kept up to date with a ResizeObserver per cell (as TruncatedTextTooltip does): a table
// can render hundreds of rows, and nothing needs to know about truncation until someone hovers.
// Same white surface as the card's truncated-title popover.
const TruncatedCellText = ({ text }: TruncatedCellTextProps) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [showFull, setShowFull] = useState(false);
  const isCutOff = () => Boolean(ref.current && ref.current.scrollWidth > ref.current.clientWidth);

  return (
    <>
      <span
        ref={ref}
        className='table-renderer-cell-text'
        onMouseEnter={() => setShowFull(isCutOff())}
        onMouseLeave={() => setShowFull(false)}
      >
        {text}
      </span>
      {showFull && (
        <Overlay target={() => ref.current} show placement={Placement.BOTTOM_START}>
          <Popover className='info-tooltip-popover chart-tooltip'>
            <Popover.Body>{text}</Popover.Body>
          </Popover>
        </Overlay>
      )}
    </>
  );
};

// Hover-anywhere-on-the-header, so a column's definition is reachable without spending header width
// on an icon that would compete with the sort arrow. NOT `InfoTooltip`, which renders its own
// IconButton trigger and therefore cannot serve a whole-header target.
//
// A11y: React's onFocus bubbles, so tabbing to the inner sort button fires this trigger and keyboard
// users reach the tooltip on every SORTABLE column. A non-sortable column has no focusable element
// and is hover-only -- a known gap, accepted because closing it means making a non-interactive `th`
// tabbable, and it is the same gap every other tooltip in this app has.
const HEADER_TOOLTIP_DELAY = { show: 400, hide: 100 };

interface ColumnHeaderTooltipProps extends PropsWithChildren {
  col: PreparedTableColumn;
}

const ColumnHeaderTooltip = ({ col, children }: ColumnHeaderTooltipProps) => {
  if (!col.description) return children;
  const { description, displayFormula } = col.description;

  return (
    <OverlayTrigger
      trigger={['hover', 'focus']}
      placement={Placement.BOTTOM}
      // The open delay is load-bearing, not polish: "hover anywhere" means the popover would
      // otherwise fire every time the user moves onto the header to click sort.
      delay={HEADER_TOOLTIP_DELAY}
      overlay={
        <Popover className='info-tooltip-popover chart-tooltip header-tooltip'>
          <Popover.Body>
            <div className='header-tooltip-term'>{col.label}</div>
            <div className='header-tooltip-definition'>{description}</div>
            {displayFormula && <div className='header-tooltip-formula'>{displayFormula}</div>}
          </Popover.Body>
        </Popover>
      }
    >
      <span className='table-renderer-header-target'>{children}</span>
    </OverlayTrigger>
  );
};

interface ColumnHeaderProps {
  col: PreparedTableColumn;
  sort?: ColumnSort;
  onToggleSort: (key: string) => void;
}

// One header cell's contents: the sortable-vs-bare branch, wrapped in its tooltip when the column
// resolved one. Extracted from the `th` because that site was already a ternary -- a third axis
// inline would make it unreadable.
const ColumnHeader = ({ col, sort, onToggleSort }: ColumnHeaderProps) => (
  <ColumnHeaderTooltip col={col}>
    {col.sortable ? (
      <button type='button' className='table-renderer-sort' onClick={() => onToggleSort(col.key)}>
        {col.label}
        {/* Always rendered so the arrow's slot is reserved and header widths never shift on click.
            Visibility and color are CSS's job, keyed off aria-sort on the th — no second source of
            truth for "is this column sorted". Unsorted falls through to the downward arrow, which
            CSS shows ghosted on hover; matches MetricsTable.tsx:1424. */}
        <span className='table-renderer-sort-icon'>
          {sort?.key === col.key && sort.dir === 'asc' ? <MdArrowUpward /> : <MdArrowDownward />}
        </span>
      </button>
    ) : (
      <span>{col.label}</span>
    )}
  </ColumnHeaderTooltip>
);

// Renders a `table` ChartConfig + its QueryResponse as a simple data grid. Dimension cells show
// raw values (dates as their ISO string); metric cells are right-aligned and formatted via the
// shared ValueFormat. Search input above the table narrows the displayed rows. The grand-total footer
// always renders the sibling 0-dimension totals query's SQL values — the client never aggregates it.
// Pivot is deferred (POC).
export const TableRenderer = ({
  config,
  data,
  className,
  showTitle = true,
  ctx
}: TableRendererProps) => {
  // Transient view state: which column the user clicked and in which direction. Deliberately NOT in
  // the config, controls, or the react-query key — sort is a permutation of rows already in memory,
  // and writing it to the config would force a refetch plus a full DuckDB re-run.
  const [sort, setSort] = useState<ColumnSort>(null);

  // Prepare the full table (unfiltered) to get structure. Memoized so a sort click (or a keystroke
  // in the search box) doesn't redo the O(rows x columns) row zip.
  const prepared = useMemo(() => prepareTable(config, data, ctx, sort), [config, data, ctx, sort]);

  // desc -> asc -> off. Clicking a different column starts that column at desc: the first click on a
  // metric almost always means "show me the biggest", so leading with descending saves a click.
  const toggleSort = (key: string) =>
    setSort((prev): ColumnSort => {
      if (prev?.key !== key) return { key, dir: 'desc' };
      if (prev.dir === 'desc') return { key, dir: 'asc' };
      return null;
    });

  // 'ascending' / 'descending' on the sorted column, 'none' everywhere else. A single ternary, so it
  // stays clear of no-nested-ternary.
  const ariaSort = (key: string): 'ascending' | 'descending' | 'none' => {
    if (sort?.key !== key) return 'none';
    return sort.dir === 'asc' ? 'ascending' : 'descending';
  };

  // The dimensions this table can be searched by, in both the browser's form (row keys) and the
  // server's (registry ids). Memoized because `rowKeys` is a dependency of the row-filter memo below,
  // and a fresh array each render would redo the filter on every keystroke elsewhere in the page.
  const searchScope = useMemo(
    () => getTableSearchScope(config, prepared?.columns ?? []),
    [config, prepared?.columns]
  );

  // Initialize hook with empty defaults (before conditional) to comply with rules of hooks
  const { searchTerm, debouncedSearchTerm, filteredRows, handleSearch } = useFilteredTable(
    prepared?.rows ?? [],
    searchScope.rowKeys
  );

  // A search is "active" only once the debounce has settled, so the footer and the body always
  // describe the same term rather than the footer trailing a keystroke behind.
  const isSearching = normalizeSearchTerm(debouncedSearchTerm).length > 0;
  const hasMatches = filteredRows.length > 0;

  // A split-grain filter can't be re-tested on only the searched rows (see searchTotalsUnavailable),
  // so while searching such a table shows NO footer and issues no totals request.
  const hideFooterWhileSearching = useMemo(
    () => isSearching && searchTotalsUnavailable(config, searchScope),
    [config, searchScope, isSearching]
  );

  // Zero matches issues NO request: the browser already knows the total is "nothing", and asking the
  // server to confirm it costs a round trip and a Redis entry per distinct dead-end term.
  const totalsConfig = useMemo(
    () =>
      isSearching && hasMatches && !hideFooterWhileSearching
        ? buildSearchTotalsConfig(
            withAppliedDashboardFilters(config, data),
            searchScope,
            debouncedSearchTerm
          )
        : null,
    [
      config,
      data,
      searchScope,
      debouncedSearchTerm,
      isSearching,
      hasMatches,
      hideFooterWhileSearching
    ]
  );

  const { cid: company } = useCompanyInfo();
  const {
    data: searchTotals,
    isFetching: isFetchingTotals,
    isError: totalsFailed
  } = useSearchTotals(company, totalsConfig);

  if (!prepared && tableSpansBlocks(config, data)) {
    return <ChartPlaceholder className={className} message={UNMERGED_TABLE_MESSAGE} />;
  }
  if (!prepared) {
    return (
      <div className={cn('chart-renderer', 'chart-renderer-placeholder', className)}>
        <p className='chart-renderer-placeholder-message'>
          Couldn&apos;t map this table&apos;s data to its encoding.
        </p>
      </div>
    );
  }

  const { columns, columnGroups, truncated, footer: idleFooter, buildFooterFrom } = prepared;

  // Same column rules as the idle footer, different numbers — see prepareTable's buildFooterFrom.
  const searchedFooter = searchTotals ? buildFooterFrom(searchTotals) : undefined;
  const footerState = resolveFooterState({
    isSearching,
    hasMatches,
    isFetching: isFetchingTotals,
    isError: totalsFailed,
    searchedFooter
  });
  // In every non-VALUES state the idle footer supplies only the SHAPE (which columns carry a total,
  // where the label sits); FooterCell renders the skeleton / em-dash / N/A over it rather than its
  // numbers, so a stale total is never on screen next to a narrowed table.
  const activeFooter =
    footerState === FooterState.VALUES && searchedFooter ? searchedFooter : idleFooter;
  const footerToRender = hideFooterWhileSearching ? undefined : activeFooter;

  return (
    <div className={cn('chart-renderer', 'table-renderer', className)}>
      {showTitle && <h3 className='chart-renderer-title'>{config.title}</h3>}
      {/* No searchable dimensions (a table grouped only by date, or by nothing) means the totals
          query cannot be scoped to whatever the user types — so don't offer a search box rather than
          offer one whose footer would be wrong. */}
      {searchScope.rowKeys.length > 0 && (
        <div className='table-renderer-search'>
          <Form.Control
            type='text'
            placeholder='Search rows...'
            value={searchTerm}
            onChange={e => handleSearch(e.target.value)}
            className='table-renderer-search-input'
          />
        </div>
      )}
      <div className='table-renderer-scroll'>
        <Table
          className={cn('table-renderer-table', { 'has-column-groups': Boolean(columnGroups) })}
          hover
        >
          <thead>
            {columnGroups && <TableSuperHeaderRow groups={columnGroups} />}
            <tr>
              {columns.map(col => (
                <th
                  key={col.key}
                  className={cn({ 'is-numeric': col.isMetric })}
                  aria-sort={ariaSort(col.key)}
                >
                  <ColumnHeader col={col} sort={sort} onToggleSort={toggleSort} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className='text-center'>
                  No rows match &quot;{searchTerm}&quot;
                </td>
              </tr>
            ) : (
              filteredRows.map((row, i) => (
                <tr key={`${columns.map(c => row[c.key]).join('|')}-${i}`}>
                  {columns.map(col => (
                    <td key={col.key} className={cn({ 'is-numeric': col.isMetric })}>
                      <HeatCell col={col} value={row[col.key]}>
                        <CellText col={col} value={row[col.key]} />
                      </HeatCell>
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
          {footerToRender && (
            <tfoot>
              <TableFooterRow columns={columns} footer={footerToRender} state={footerState} />
            </tfoot>
          )}
        </Table>
      </div>
      {truncated > 0 && (
        <p className='table-renderer-note'>
          {truncationNote(filteredRows.length, truncated, Boolean(footerToRender), isSearching)}
        </p>
      )}
    </div>
  );
};
